import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient, type MyProviderRow } from "./api";
import { getSession } from "./auth";

export interface ProviderSwitcher {
  /** Orgs the operator belongs to. Empty until loaded. */
  providers: MyProviderRow[];
  activeProviderId: string | null;
  activeProvider: MyProviderRow | null;
  /** Switch the active provider (no-op if it's already active). */
  switchTo: (providerId: string) => void;
  isSwitching: boolean;
  /** True only when the operator belongs to more than one org. */
  canSwitch: boolean;
}

/**
 * Single source of truth for the provider switcher, shared by the top bar and
 * the sidebar. Reads memberships from `GET /api/team/my-providers` and the
 * active provider from the session (`/api/me`), and switches via
 * `POST /api/team/switch`. Both `useQuery` calls share react-query's cache by
 * key, so mounting this in two places doesn't double-fetch.
 *
 * On switch we clear the whole cache and reload, since the active provider
 * changes every provider-scoped query plus non-React state (env/tweaks).
 */
export function useProviderSwitcher(): ProviderSwitcher {
  const queryClient = useQueryClient();

  const { data: session } = useQuery({
    queryKey: ["session"],
    queryFn: getSession,
    retry: false,
    staleTime: 60_000,
  });

  const { data: myProviders } = useQuery({
    queryKey: ["team", "my-providers"],
    queryFn: () => apiClient.team.myProviders(),
    retry: false,
    staleTime: 60_000,
  });

  const switchMutation = useMutation({
    mutationFn: (providerId: string) => apiClient.team.switch(providerId),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      window.location.reload();
    },
  });

  const providers = myProviders ?? [];
  const activeProviderId =
    session?.currentProviderId ?? providers[0]?.id ?? null;
  const activeProvider =
    providers.find((p) => p.id === activeProviderId) ?? providers[0] ?? null;

  return {
    providers,
    activeProviderId,
    activeProvider,
    switchTo: (providerId: string) => {
      if (providerId !== activeProviderId) switchMutation.mutate(providerId);
    },
    isSwitching: switchMutation.isPending,
    canSwitch: providers.length > 1,
  };
}
